import { Controller } from "@hotwired/stimulus"

// Notifications on this device while the game is closed (app/models/push.rb,
// the service worker). The browser is asked for permission only when the
// player presses Turn on, never on page load.
//
// A browser can still hold a subscription made under someone else's sign-in
// (a shared computer), so it is sent to the server again only when the server
// says this sign-in turned notifications on.
//
// Children with data-push-when="off denied" show only in those states. With
// the offer value set, the whole element is the chat list's one-time offer:
// it shows only to someone who could turn them on and has not said Not now.
const DISMISSED = "lairlings:push-offer"

export default class extends Controller {
  static values = { key: String, on: Boolean, url: String, offer: Boolean }
  static targets = ["error"]

  connect() {
    this.show(this.firstState())
    if (this.offerValue) {
      this.element.hidden = !["off", "homescreen"].includes(this.state) || dismissed()
      return
    }
    if (this.state === "on") this.resync()
  }

  // An iPhone in a Safari tab has no push at all; added to the Home Screen it
  // does, so the offer there is the steps to add it, not a dead button.
  firstState() {
    if (!supported()) return homeScreenFirst() ? "homescreen" : "unsupported"
    if (Notification.permission === "denied") return "denied"
    return this.onValue && Notification.permission === "granted" ? "on" : "off"
  }

  async turnOn() {
    this.fail("")
    this.show("working")
    try {
      const permission = await Notification.requestPermission()
      if (permission !== "granted") return this.show(permission === "denied" ? "denied" : "off")

      await this.send("POST", await this.subscription())
      this.show("on")
    } catch (_) {
      this.show("off")
      this.fail("Notifications could not be turned on. Try again in a moment.")
    }
  }

  async turnOff() {
    this.fail("")
    this.show("working")
    try {
      const registration = await navigator.serviceWorker.getRegistration("/")
      const subscription = registration && await registration.pushManager.getSubscription()
      await this.send("DELETE", subscription)
      if (subscription) await subscription.unsubscribe()
      this.show("off")
    } catch (_) {
      this.show("on")
      this.fail("Notifications could not be turned off. Try again in a moment.")
    }
  }

  notNow() {
    try { window.localStorage.setItem(DISMISSED, "1") } catch (_) {}
    this.element.hidden = true
  }

  // A browser can drop or replace its subscription on its own; tell the server
  // the current one each time, quietly.
  async resync() {
    try {
      await this.send("POST", await this.subscription())
    } catch (_) {}
  }

  // This browser's subscription under the game's key, made if there is none.
  // One under an old key cannot be pushed to, so it is replaced.
  async subscription() {
    await navigator.serviceWorker.register("/service-worker", { scope: "/" })
    const registration = await navigator.serviceWorker.ready
    const key = fromBase64Url(this.keyValue)
    const existing = await registration.pushManager.getSubscription()
    if (existing) {
      if (sameKey(existing.options && existing.options.applicationServerKey, key)) return existing
      await existing.unsubscribe()
    }
    return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  }

  async send(method, subscription) {
    const response = await fetch(this.urlValue, {
      method,
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      body: JSON.stringify({ subscription: subscription ? subscription.toJSON() : {} })
    })
    if (!response.ok) throw new Error(`push ${method} ${response.status}`)
  }

  show(state) {
    this.state = state
    this.element.dataset.pushState = state
    this.element.querySelectorAll("[data-push-when]").forEach((node) => {
      node.hidden = !node.dataset.pushWhen.split(" ").includes(state)
    })
  }

  fail(line) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = line
    this.errorTarget.hidden = !line
  }
}

function supported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
}

export function homeScreenFirst(nav = navigator, win = window) {
  const apple = /iPhone|iPad|iPod/.test(nav.userAgent || "") ||
    (nav.platform === "MacIntel" && nav.maxTouchPoints > 1)
  const standalone = nav.standalone === true ||
    (typeof win.matchMedia === "function" && win.matchMedia("(display-mode: standalone)").matches)
  return apple && !standalone
}

function dismissed() {
  try { return window.localStorage.getItem(DISMISSED) === "1" } catch (_) { return false }
}

export function fromBase64Url(text) {
  const base64 = (text + "===".slice((text.length + 3) % 4)).replace(/-/g, "+").replace(/_/g, "/")
  return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
}

export function sameKey(buffer, key) {
  if (!buffer) return false
  const bytes = new Uint8Array(buffer)
  return bytes.length === key.length && bytes.every((byte, index) => byte === key[index])
}
